/**
 * SchoolLibrary — Physical Books + Digital Resources (eBook, Audio, Video)
 * In-app viewing only, fee-gated access, no downloads to personal devices
 */
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import { MediaUpload } from '@/components/MediaUpload';
import {
  BookOpen, Plus, Search, Loader2, RotateCcw, AlertTriangle, Trash2,
  BookMarked, Archive, Edit2, ArrowLeft, Library, Clock, History,
  FileText, Headphones, Video, Film, Lock, Eye, Play, Pause,
  Volume2, Maximize, ShieldAlert, Store, ClipboardList, UserPlus, CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, { ...opts, headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const RESOURCE_TYPES = [
  { value: 'physical', label: 'Physical Book', icon: BookOpen, color: 'bg-slate-100 text-slate-700' },
  { value: 'ebook', label: 'eBook', icon: FileText, color: 'bg-blue-100 text-blue-700' },
  { value: 'audio', label: 'Audio', icon: Headphones, color: 'bg-purple-100 text-purple-700' },
  { value: 'video', label: 'Video', icon: Video, color: 'bg-rose-100 text-rose-700' },
];
const RES_MAP = Object.fromEntries(RESOURCE_TYPES.map(r => [r.value, r]));

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card>
    <CardContent className="p-3 text-center">
      <Icon className={`h-5 w-5 mx-auto mb-1 text-${color}-500`} />
      <p className="text-xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </CardContent>
  </Card>
);


// ==================== MEDIA VIEWER (NO DOWNLOAD) ====================
const SecureMediaViewer = ({ resource, onClose }) => {
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  // Prevent right-click
  const preventContext = (e) => e.preventDefault();

  if (!resource || !resource.access_granted) return null;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden" onContextMenu={preventContext} aria-describedby="media-viewer-desc">
        <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {resource.resource_type === 'ebook' && <FileText className="h-5 w-5 text-blue-600" />}
            {resource.resource_type === 'audio' && <Headphones className="h-5 w-5 text-purple-600" />}
            {resource.resource_type === 'video' && <Video className="h-5 w-5 text-rose-600" />}
            <div>
              <DialogTitle className="text-sm font-semibold">{resource.title}</DialogTitle>
              <p id="media-viewer-desc" className="text-xs text-slate-500">In-app viewing only — sharing and downloading disabled</p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs gap-1">
            <Lock className="h-3 w-3" /> Protected Content
          </Badge>
        </div>

        <div
          className="relative bg-black"
          style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
          onContextMenu={preventContext}
          data-testid="secure-media-container"
        >
          {resource.resource_type === 'ebook' && (
            <iframe
              src={resource.file_url}
              className="w-full border-0"
              style={{ height: 'calc(90vh - 80px)' }}
              sandbox="allow-same-origin allow-scripts"
              title={resource.title}
              data-testid="ebook-viewer"
            />
          )}

          {resource.resource_type === 'audio' && (
            <div className="flex flex-col items-center justify-center py-20 px-8 bg-gradient-to-b from-purple-950 to-slate-950">
              <div className="w-32 h-32 rounded-full bg-purple-900/50 flex items-center justify-center mb-6 animate-pulse">
                <Headphones className="h-16 w-16 text-purple-300" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">{resource.title}</h3>
              {resource.duration_minutes && (
                <p className="text-purple-300 text-sm mb-6">{resource.duration_minutes} minutes</p>
              )}
              <audio
                ref={audioRef}
                controls
                controlsList="nodownload"
                className="w-full max-w-md"
                onContextMenu={preventContext}
                data-testid="audio-player"
              >
                <source src={resource.file_url} type={resource.content_type ? `audio/${resource.content_type}` : 'audio/mpeg'} />
                Your browser does not support audio playback.
              </audio>
            </div>
          )}

          {resource.resource_type === 'video' && (
            <div className="flex items-center justify-center bg-black" style={{ height: 'calc(90vh - 80px)' }}>
              <video
                ref={videoRef}
                controls
                controlsList="nodownload nofullscreen"
                disablePictureInPicture
                className="max-h-full max-w-full"
                onContextMenu={preventContext}
                data-testid="video-player"
              >
                <source src={resource.file_url} type={resource.content_type ? `video/${resource.content_type}` : 'video/mp4'} />
                Your browser does not support video playback.
              </video>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ==================== BOOK FORM DIALOG ====================
const BookFormDialog = ({ open, onClose, tenantId, editing }) => {
  const qc = useQueryClient();
  const defaults = {
    title: '', author: '', isbn: '', category: '', publisher: '', year: null,
    total_copies: 1, shelf_location: '', description: '',
    resource_type: 'physical', file_url: '', content_type: '', duration_minutes: null,
    requires_fee_clearance: true,
  };
  const [form, setForm] = useState(defaults);

  useEffect(() => {
    if (open) setForm(editing ? {
      title: editing.title || '', author: editing.author || '', isbn: editing.isbn || '',
      category: editing.category || '', publisher: editing.publisher || '',
      year: editing.year || null, total_copies: editing.total_copies || 1,
      shelf_location: editing.shelf_location || '', description: editing.description || '',
      resource_type: editing.resource_type || 'physical', file_url: editing.file_url || '',
      content_type: editing.content_type || '',
      duration_minutes: editing.duration_minutes || null,
      requires_fee_clearance: editing.requires_fee_clearance !== false,
    } : defaults);
  }, [open, editing]);

  const isDigital = form.resource_type !== 'physical';

  const mutation = useMutation({
    mutationFn: d => editing
      ? api(`/tenants/${tenantId}/library/books/${editing.id}`, { method: 'PUT', body: JSON.stringify(d) })
      : api(`/tenants/${tenantId}/library/books`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success(editing ? 'Resource updated' : 'Resource added'); qc.invalidateQueries({ queryKey: ['library-books'] }); qc.invalidateQueries({ queryKey: ['library-dashboard'] }); onClose(); },
    onError: e => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="book-form-desc">
        <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} {isDigital ? 'Digital Resource' : 'Book'}</DialogTitle><p id="book-form-desc" className="sr-only">Form to add or edit a library resource</p></DialogHeader>
        <div className="space-y-3">
          {/* Resource Type */}
          <div>
            <Label>Resource Type *</Label>
            <Select value={form.resource_type} onValueChange={v => setForm({ ...form, resource_type: v })}>
              <SelectTrigger data-testid="resource-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RESOURCE_TYPES.map(r => (
                  <SelectItem key={r.value} value={r.value}>
                    <span className="flex items-center gap-2">{r.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="book-title" /></div>
            <div><Label>Author</Label><Input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label>ISBN</Label><Input value={form.isbn} onChange={e => setForm({ ...form, isbn: e.target.value })} /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="Fiction, Science..." /></div>
            {!isDigital && <div><Label>Copies</Label><Input type="number" min="1" value={form.total_copies} onChange={e => setForm({ ...form, total_copies: parseInt(e.target.value) || 1 })} /></div>}
            {isDigital && (
              <div><Label>Content Type</Label>
                <Select value={form.content_type} onValueChange={v => setForm({ ...form, content_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {form.resource_type === 'ebook' && <><SelectItem value="pdf">PDF</SelectItem><SelectItem value="epub">EPUB</SelectItem></>}
                    {form.resource_type === 'audio' && <><SelectItem value="mp3">MP3</SelectItem><SelectItem value="wav">WAV</SelectItem><SelectItem value="ogg">OGG</SelectItem></>}
                    {form.resource_type === 'video' && <><SelectItem value="mp4">MP4</SelectItem><SelectItem value="webm">WebM</SelectItem></>}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {!isDigital && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label>Shelf Location</Label><Input value={form.shelf_location} onChange={e => setForm({ ...form, shelf_location: e.target.value })} placeholder="A-3" /></div>
              <div><Label>Publisher</Label><Input value={form.publisher} onChange={e => setForm({ ...form, publisher: e.target.value })} /></div>
              <div><Label>Year</Label><Input type="number" value={form.year || ''} onChange={e => setForm({ ...form, year: parseInt(e.target.value) || null })} placeholder="2024" /></div>
            </div>
          )}

          {isDigital && (
            <>
              <div>
                <MediaUpload value={form.file_url} onChange={(url) => setForm({ ...form, file_url: url })} purpose="general" label="Content file *" hint="Upload the PDF, audio or video — it is streamed in-app only" testId="library-file-upload" />
              </div>
              {(form.resource_type === 'audio' || form.resource_type === 'video') && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div><Label>Duration (min)</Label><Input type="number" value={form.duration_minutes || ''} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || null })} /></div>
                  <div><Label>Publisher</Label><Input value={form.publisher} onChange={e => setForm({ ...form, publisher: e.target.value })} /></div>
                  <div><Label>Year</Label><Input type="number" value={form.year || ''} onChange={e => setForm({ ...form, year: parseInt(e.target.value) || null })} /></div>
                </div>
              )}
              {form.resource_type === 'ebook' && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Publisher</Label><Input value={form.publisher} onChange={e => setForm({ ...form, publisher: e.target.value })} /></div>
                  <div><Label>Year</Label><Input type="number" value={form.year || ''} onChange={e => setForm({ ...form, year: parseInt(e.target.value) || null })} /></div>
                </div>
              )}
              <div className="flex items-center gap-2 p-2 bg-amber-50 rounded border border-amber-200">
                <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0" />
                <p className="text-xs text-amber-800">
                  Fee-gated: Students must have all fees cleared to access this resource.
                  Content is viewable within the system only — no downloads or sharing.
                </p>
              </div>
            </>
          )}

          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Brief description..." /></div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => mutation.mutate(form)} disabled={!form.title || (isDigital && !form.file_url) || mutation.isPending} data-testid="save-book-btn">
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{editing ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ==================== BOOK/RESOURCE DETAIL VIEW ====================
const BookDetail = ({ tenantId, book, onBack }) => {
  const qc = useQueryClient();
  const [viewingMedia, setViewingMedia] = useState(null);

  const { data: detail } = useQuery({
    queryKey: ['library-book-detail', tenantId, book.id],
    queryFn: () => api(`/tenants/${tenantId}/library/books/${book.id}`),
  });
  const { data: bookBorrows = [] } = useQuery({
    queryKey: ['library-book-borrows', tenantId, book.id],
    queryFn: () => api(`/tenants/${tenantId}/library/borrows`).then(all => all.filter(b => b.book_id === book.id)),
  });

  const returnMutation = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/library/return/${id}`, { method: 'POST' }),
    onSuccess: r => { toast.success(r.was_overdue ? 'Returned (was overdue)' : 'Book returned'); qc.invalidateQueries({ queryKey: ['library-book-borrows'] }); qc.invalidateQueries({ queryKey: ['library-book-detail'] }); qc.invalidateQueries({ queryKey: ['library-books'] }); qc.invalidateQueries({ queryKey: ['library-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const handlePreview = async () => {
    try {
      const data = await api(`/tenants/${tenantId}/library/resources/${book.id}/access`);
      if (data.access_granted) {
        setViewingMedia(data);
      } else {
        toast.error(data.reason || 'Access denied');
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const d = detail || book;
  const isDigital = d.resource_type && d.resource_type !== 'physical';
  const resInfo = RES_MAP[d.resource_type] || RES_MAP.physical;
  const ResIcon = resInfo.icon;

  return (
    <div className="space-y-4" data-testid="book-detail">
      <button onClick={onBack} className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back to Catalog</button>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg ${resInfo.color} flex items-center justify-center shrink-0`}>
                <ResIcon className="h-5 w-5" />
              </div>
              <div>
                <CardTitle>{d.title}</CardTitle>
                <p className="text-sm text-slate-500">{d.author} {d.year ? `(${d.year})` : ''} {d.publisher ? `| ${d.publisher}` : ''}</p>
                <Badge className={`mt-1 ${resInfo.color}`}>{resInfo.label}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isDigital && d.file_url && (
                <Button size="sm" onClick={handlePreview} data-testid="preview-resource-btn">
                  <Eye className="h-4 w-4 mr-1" /> Preview
                </Button>
              )}
              {!isDigital && (
                <Badge className={d.available_copies > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {d.available_copies}/{d.total_copies} available
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
            {d.isbn && <span><span className="text-slate-400">ISBN:</span> {d.isbn}</span>}
            {d.category && <span><span className="text-slate-400">Category:</span> <Badge variant="outline" className="text-xs">{d.category}</Badge></span>}
            {!isDigital && d.shelf_location && <span><span className="text-slate-400">Shelf:</span> {d.shelf_location}</span>}
            {isDigital && d.content_type && <span><span className="text-slate-400">Format:</span> {d.content_type.toUpperCase()}</span>}
            {isDigital && d.duration_minutes && <span><span className="text-slate-400">Duration:</span> {d.duration_minutes} min</span>}
          </div>
          {d.description && <p className="text-sm text-slate-600">{d.description}</p>}

          {isDigital && (
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border text-sm">
              <Lock className="h-4 w-4 text-slate-500 shrink-0" />
              <span className="text-slate-600">
                Fee-gated access: Students must have cleared fees. Content viewable within system only — no downloads or sharing.
              </span>
            </div>
          )}

          {!isDigital && (
            <div className="border-t pt-3">
              <h3 className="text-sm font-semibold mb-2">Borrow History ({bookBorrows.length})</h3>
              {bookBorrows.length === 0 ? (
                <p className="text-sm text-slate-400">No borrow history for this book</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Adm No.</TableHead>
                      <TableHead>Borrowed</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead>Returned</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookBorrows.map(b => (
                      <TableRow key={b.id}>
                        <TableCell className="font-medium text-sm">{b.student_name}</TableCell>
                        <TableCell className="text-xs text-slate-500">{b.admission_number}</TableCell>
                        <TableCell className="text-xs">{b.borrowed_date}</TableCell>
                        <TableCell className="text-xs">{b.due_date}</TableCell>
                        <TableCell className="text-xs">{b.returned_date || '-'}</TableCell>
                        <TableCell>
                          {b.status === 'returned' ? (
                            <Badge className={b.was_overdue ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>{b.was_overdue ? 'Late Return' : 'Returned'}</Badge>
                          ) : b.is_overdue ? (
                            <Badge className="bg-red-100 text-red-700">Overdue</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700">Borrowed</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {b.status === 'borrowed' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { if (window.confirm('Return this book?')) returnMutation.mutate(b.id); }}>
                              <RotateCcw className="h-3 w-3 mr-1" />Return
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {viewingMedia && <SecureMediaViewer resource={viewingMedia} onClose={() => setViewingMedia(null)} />}
    </div>
  );
};


// ==================== MAIN COMPONENT ====================
export const SchoolLibrary = ({ tenantId }) => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('');
  const [showBookForm, setShowBookForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showBorrowForm, setShowBorrowForm] = useState(false);
  const [borrowForm, setBorrowForm] = useState({ student_id: '', book_id: '', due_date: '', notes: '' });
  const [selectedBook, setSelectedBook] = useState(null);
  const [borrowFilter, setBorrowFilter] = useState('borrowed');
  const [assignTarget, setAssignTarget] = useState(null); // book selected for Assign dialog

  const queryParams = new URLSearchParams();
  if (search) queryParams.set('search', search);
  if (categoryFilter) queryParams.set('category', categoryFilter);
  if (resourceTypeFilter) queryParams.set('resource_type', resourceTypeFilter);
  const qStr = queryParams.toString() ? `?${queryParams}` : '';

  const { data: dashboard } = useQuery({ queryKey: ['library-dashboard', tenantId], queryFn: () => api(`/tenants/${tenantId}/library/dashboard`) });
  const { data: books = [], isLoading } = useQuery({ queryKey: ['library-books', tenantId, search, categoryFilter, resourceTypeFilter], queryFn: () => api(`/tenants/${tenantId}/library/books${qStr}`) });
  const { data: categories = [] } = useQuery({ queryKey: ['library-categories', tenantId], queryFn: () => api(`/tenants/${tenantId}/library/categories`) });
  const { data: borrows = [] } = useQuery({ queryKey: ['library-borrows', tenantId, borrowFilter], queryFn: () => api(`/tenants/${tenantId}/library/borrows${borrowFilter !== 'all' ? `?status=${borrowFilter}` : ''}`) });
  const { data: overdue = [] } = useQuery({ queryKey: ['library-overdue', tenantId], queryFn: () => api(`/tenants/${tenantId}/library/overdue`) });
  const { data: students = [] } = useQuery({ queryKey: ['library-students', tenantId], queryFn: () => api(`/tenants/${tenantId}/students`).then(d => d.students || d).catch(() => []) });
  const { data: accessLog = [] } = useQuery({ queryKey: ['library-access-log', tenantId], queryFn: () => api(`/tenants/${tenantId}/library/resources/access-log`) });

  const borrowBook = useMutation({
    mutationFn: d => api(`/tenants/${tenantId}/library/borrow`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: r => { toast.success(`Book issued, due: ${r.due_date}`); qc.invalidateQueries({ queryKey: ['library-borrows'] }); qc.invalidateQueries({ queryKey: ['library-books'] }); qc.invalidateQueries({ queryKey: ['library-dashboard'] }); setShowBorrowForm(false); setBorrowForm({ student_id: '', book_id: '', due_date: '', notes: '' }); },
    onError: e => toast.error(e.message),
  });

  const returnBook = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/library/return/${id}`, { method: 'POST' }),
    onSuccess: r => { toast.success(r.was_overdue ? 'Returned (was overdue)' : 'Book returned'); qc.invalidateQueries({ queryKey: ['library-borrows'] }); qc.invalidateQueries({ queryKey: ['library-books'] }); qc.invalidateQueries({ queryKey: ['library-overdue'] }); qc.invalidateQueries({ queryKey: ['library-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const deleteBook = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/library/books/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Resource deleted'); qc.invalidateQueries({ queryKey: ['library-books'] }); qc.invalidateQueries({ queryKey: ['library-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const [publishTarget, setPublishTarget] = useState(null);
  const [publishForm, setPublishForm] = useState({ price: 0, rental_price: 0, is_free: false, status: 'published' });
  const publishToShop = useMutation({
    mutationFn: ({ id, body }) => api(`/tenants/${tenantId}/library/books/${id}/publish-to-bookshop`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: r => {
      toast.success(r.created ? `Published "${r.title}" to Bookshop` : `Updated "${r.title}" in Bookshop`);
      qc.invalidateQueries({ queryKey: ['library-books'] });
      setPublishTarget(null);
    },
    onError: e => toast.error(e.message),
  });
  const unpublishFromShop = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/library/books/${id}/unpublish-from-bookshop`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Hidden from Bookshop (still linked)');
      qc.invalidateQueries({ queryKey: ['library-books'] });
      setPublishTarget(null);
    },
    onError: e => toast.error(e.message),
  });
  const syncFromShop = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/library/books/${id}/sync-from-bookshop`, { method: 'POST' }),
    onSuccess: r => {
      toast.success(`Synced from Bookshop · price $${r.bookshop_price?.toFixed?.(2) ?? r.bookshop_price} · ${r.bookshop_status}`);
      qc.invalidateQueries({ queryKey: ['library-books'] });
      setPublishForm(f => ({ ...f, price: r.bookshop_price || 0, status: r.bookshop_status || 'published', is_free: !!r.bookshop_is_free, rental_price: r.bookshop_rental_price || 0 }));
    },
    onError: e => toast.error(e.message),
  });

  if (selectedBook) return <BookDetail tenantId={tenantId} book={selectedBook} onBack={() => setSelectedBook(null)} />;

  return (
    <div className="space-y-5" data-testid="school-library">
      <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Library className="h-5 w-5" /> Library & Digital Resources</h2>

      {/* Dashboard */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="library-dashboard">
          <StatCard label="Total" value={dashboard.total_books} icon={BookOpen} color="indigo" />
          <StatCard label="Physical" value={dashboard.physical} icon={BookOpen} color="slate" />
          <StatCard label="eBooks" value={dashboard.ebooks} icon={FileText} color="blue" />
          <StatCard label="Audio" value={dashboard.audio} icon={Headphones} color="purple" />
          <StatCard label="Video" value={dashboard.video} icon={Video} color="rose" />
          <StatCard label="Borrowed" value={dashboard.borrowed} icon={BookMarked} color="amber" />
          <StatCard label="Overdue" value={dashboard.overdue} icon={AlertTriangle} color="red" />
          <StatCard label="Categories" value={dashboard.categories} icon={Library} color="slate" />
        </div>
      )}

      <Tabs defaultValue="catalog">
        <TabsList className="overflow-x-auto flex-nowrap">
          <TabsTrigger value="catalog" data-testid="library-tab-catalog"><BookOpen className="h-4 w-4 mr-1" /> Catalog</TabsTrigger>
          <TabsTrigger value="digital" data-testid="library-tab-digital"><Film className="h-4 w-4 mr-1" /> Digital</TabsTrigger>
          <TabsTrigger value="borrowed" data-testid="library-tab-borrowed"><BookMarked className="h-4 w-4 mr-1" /> Borrowed</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="library-tab-assignments"><ClipboardList className="h-4 w-4 mr-1" /> Reading Lists</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="library-tab-overdue"><AlertTriangle className="h-4 w-4 mr-1" /> Overdue {overdue.length > 0 && <Badge className="ml-1 bg-red-500 text-white text-[10px] h-4 min-w-4 px-1">{overdue.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="access-log" data-testid="library-tab-access-log"><Eye className="h-4 w-4 mr-1" /> Access Log</TabsTrigger>
          <TabsTrigger value="history" data-testid="library-tab-history"><History className="h-4 w-4 mr-1" /> History</TabsTrigger>
        </TabsList>

        {/* CATALOG TAB */}
        <TabsContent value="catalog" className="mt-4 space-y-3">
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input placeholder="Search resources..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" data-testid="book-search" />
            </div>
            <Select value={resourceTypeFilter || 'all'} onValueChange={v => setResourceTypeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full max-w-[130px]" data-testid="type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {RESOURCE_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={categoryFilter || 'all'} onValueChange={v => setCategoryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full max-w-[150px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Categories</SelectItem>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" onClick={() => { setEditing(null); setShowBookForm(true); }} data-testid="add-book-btn"><Plus className="h-4 w-4 mr-1" /> Add Resource</Button>
            <Button size="sm" variant="outline" onClick={() => setShowBorrowForm(true)} data-testid="borrow-book-btn"><BookMarked className="h-4 w-4 mr-1" /> Issue Book</Button>
          </div>

          {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : books.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400"><BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />No resources in catalog</CardContent></Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-center">Availability</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {books.map(b => {
                    const res = RES_MAP[b.resource_type] || RES_MAP.physical;
                    const ResIcon = res.icon;
                    const isDigital = b.resource_type && b.resource_type !== 'physical';
                    return (
                      <TableRow key={b.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedBook(b)} data-testid={`book-row-${b.id}`}>
                        <TableCell>
                          <Badge className={`${res.color} gap-1`}><ResIcon className="h-3 w-3" />{res.label}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{b.title}</span>
                            {b.bookshop_book_id && (
                              <Badge
                                className={b.bookshop_status === 'draft' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}
                                data-testid={`shop-badge-${b.id}`}
                              >
                                <Store className="h-3 w-3 mr-1" />
                                {b.bookshop_status === 'draft' ? 'In Bookshop (draft)' : 'Published'}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{b.author || '-'}</TableCell>
                        <TableCell>{b.category ? <Badge variant="outline" className="text-xs">{b.category}</Badge> : '-'}</TableCell>
                        <TableCell className="text-center">
                          {isDigital ? (
                            <Badge className="bg-green-100 text-green-700 gap-1"><Lock className="h-3 w-3" />Fee-gated</Badge>
                          ) : (
                            <Badge className={b.available_copies > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{b.available_copies}/{b.total_copies}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Assign as required reading" onClick={() => setAssignTarget(b)} data-testid={`assign-book-${b.id}`}>
                              <UserPlus className="h-3.5 w-3.5 text-emerald-600" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" title={b.bookshop_book_id ? 'Update in Bookshop' : 'Publish to Bookshop'} onClick={() => { setPublishTarget(b); setPublishForm({ price: b.bookshop_price || 0, rental_price: 0, is_free: false, status: 'published' }); }} data-testid={`publish-to-shop-${b.id}`}>
                              <Store className={`h-3.5 w-3.5 ${b.bookshop_book_id ? 'text-emerald-600' : 'text-slate-400'}`} />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(b); setShowBookForm(true); }} data-testid={`edit-book-${b.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { if (window.confirm(`Delete "${b.title}"?`)) deleteBook.mutate(b.id); }}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* DIGITAL RESOURCES TAB */}
        <TabsContent value="digital" className="mt-4 space-y-3">
          <div className="flex gap-2 flex-wrap items-center">
            <h3 className="text-sm font-semibold">Digital Resources</h3>
            <div className="flex gap-1 ml-auto">
              {['ebook', 'audio', 'video'].map(t => {
                const r = RES_MAP[t];
                const Icon = r.icon;
                const count = books.filter(b => b.resource_type === t).length;
                return (
                  <button
                    key={t}
                    onClick={() => setResourceTypeFilter(resourceTypeFilter === t ? '' : t)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      resourceTypeFilter === t ? r.color + ' border-current' : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`digital-filter-${t}`}
                  >
                    <Icon className="h-3 w-3" /> {r.label} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {(() => {
            const digitalBooks = books.filter(b => ['ebook', 'audio', 'video'].includes(b.resource_type));
            const filtered = resourceTypeFilter ? digitalBooks.filter(b => b.resource_type === resourceTypeFilter) : digitalBooks;
            return filtered.length === 0 ? (
              <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400"><Film className="h-8 w-8 mx-auto mb-2 opacity-50" />No digital resources yet</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map(b => {
                  const res = RES_MAP[b.resource_type];
                  const ResIcon = res.icon;
                  return (
                    <Card key={b.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedBook(b)} data-testid={`digital-card-${b.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-12 h-12 rounded-lg ${res.color} flex items-center justify-center shrink-0`}>
                            <ResIcon className="h-6 w-6" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-medium text-sm truncate">{b.title}</h4>
                            <p className="text-xs text-slate-500 truncate">{b.author || 'Unknown author'}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className={res.color + ' text-[10px]'}>{res.label}</Badge>
                              {b.content_type && <span className="text-[10px] text-slate-400 uppercase">{b.content_type}</span>}
                              {b.duration_minutes && <span className="text-[10px] text-slate-400">{b.duration_minutes} min</span>}
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between text-xs">
                          {b.category && <Badge variant="outline" className="text-[10px]">{b.category}</Badge>}
                          <Badge className="bg-amber-50 text-amber-700 gap-1 text-[10px]"><Lock className="h-2.5 w-2.5" />Fee required</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            );
          })()}
        </TabsContent>

        {/* BORROWED TAB */}
        <TabsContent value="borrowed" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Active Borrows ({borrows.filter(b => b.status === 'borrowed').length})</h3>
          </div>
          {borrows.filter(b => b.status === 'borrowed').length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400"><BookMarked className="h-8 w-8 mx-auto mb-2 opacity-50" />No active borrows</CardContent></Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Book</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Adm No.</TableHead>
                    <TableHead>Borrowed</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {borrows.filter(b => b.status === 'borrowed').map(b => (
                    <TableRow key={b.id} className={b.is_overdue ? 'bg-red-50/30' : ''} data-testid={`borrow-row-${b.id}`}>
                      <TableCell className="font-medium text-sm">{b.book_title}</TableCell>
                      <TableCell className="text-xs text-slate-500">{b.book_author}</TableCell>
                      <TableCell className="text-sm">{b.student_name}</TableCell>
                      <TableCell className="text-xs text-slate-500">{b.admission_number}</TableCell>
                      <TableCell className="text-xs">{b.borrowed_date}</TableCell>
                      <TableCell className="text-xs">{b.due_date}</TableCell>
                      <TableCell>
                        {b.is_overdue ? <Badge className="bg-red-100 text-red-700">Overdue</Badge> : <Badge className="bg-blue-100 text-blue-700">Active</Badge>}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { if (window.confirm(`Return "${b.book_title}"?`)) returnBook.mutate(b.id); }} data-testid={`return-${b.id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" /> Return
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* READING ASSIGNMENTS TAB */}
        <TabsContent value="assignments" className="mt-4 space-y-3">
          <AssignmentsPanel tenantId={tenantId} students={students} />
        </TabsContent>

        {/* OVERDUE TAB */}
        <TabsContent value="overdue" className="mt-4 space-y-3">
          <h3 className="text-sm font-semibold text-red-600 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Overdue Books ({overdue.length})</h3>
          {overdue.length === 0 ? (
            <Card className="border-dashed border-green-200"><CardContent className="py-10 text-center text-green-600 font-medium">No overdue books</CardContent></Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Book</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Adm No.</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-center">Days Overdue</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overdue.map(b => (
                    <TableRow key={b.id} className="bg-red-50/30" data-testid={`overdue-row-${b.id}`}>
                      <TableCell className="font-medium text-sm">{b.book_title}</TableCell>
                      <TableCell className="text-sm">{b.student_name}</TableCell>
                      <TableCell className="text-xs text-slate-500">{b.admission_number}</TableCell>
                      <TableCell className="text-xs">{b.due_date}</TableCell>
                      <TableCell className="text-center"><Badge className="bg-red-100 text-red-700">{b.days_overdue} days</Badge></TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { if (window.confirm(`Return "${b.book_title}"?`)) returnBook.mutate(b.id); }}>
                          <RotateCcw className="h-3 w-3 mr-1" /> Return
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* ACCESS LOG TAB */}
        <TabsContent value="access-log" className="mt-4 space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-1"><Eye className="h-4 w-4" /> Digital Resource Access Log</h3>
          {accessLog.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400"><Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />No access records yet</CardContent></Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Resource</TableHead>
                    <TableHead>Accessed By</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accessLog.map(l => (
                    <TableRow key={l.id} data-testid={`access-log-${l.id}`}>
                      <TableCell className="font-medium text-sm">{l.book_title}</TableCell>
                      <TableCell className="text-sm">{l.student_name}</TableCell>
                      <TableCell><Badge className={(RES_MAP[l.resource_type] || RES_MAP.physical).color + ' text-xs'}>{(RES_MAP[l.resource_type] || RES_MAP.physical).label}</Badge></TableCell>
                      <TableCell className="text-xs capitalize">{l.action}</TableCell>
                      <TableCell className="text-xs">{new Date(l.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Borrow History</h3>
            <Select value={borrowFilter} onValueChange={setBorrowFilter}>
              <SelectTrigger className="w-full max-w-[140px] h-8 text-xs" data-testid="history-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="borrowed">Active</SelectItem>
                <SelectItem value="returned">Returned</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {borrows.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400"><History className="h-8 w-8 mx-auto mb-2 opacity-50" />No borrow records</CardContent></Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Book</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Borrowed</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Returned</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {borrows.map(b => (
                    <TableRow key={b.id} data-testid={`history-row-${b.id}`}>
                      <TableCell className="font-medium text-sm">{b.book_title}</TableCell>
                      <TableCell className="text-sm">{b.student_name} <span className="text-xs text-slate-400">({b.admission_number})</span></TableCell>
                      <TableCell className="text-xs">{b.borrowed_date}</TableCell>
                      <TableCell className="text-xs">{b.due_date}</TableCell>
                      <TableCell className="text-xs">{b.returned_date || '-'}</TableCell>
                      <TableCell>
                        {b.status === 'returned' ? (
                          <Badge className={b.was_overdue ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                            {b.was_overdue ? 'Late Return' : 'Returned'}
                          </Badge>
                        ) : b.is_overdue ? (
                          <Badge className="bg-red-100 text-red-700">Overdue</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700">Active</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Book Form Dialog */}
      <BookFormDialog open={showBookForm} onClose={() => { setShowBookForm(false); setEditing(null); }} tenantId={tenantId} editing={editing} />

      {/* Assign Reading Dialog */}
      <AssignBookDialog
        tenantId={tenantId}
        book={assignTarget}
        students={Array.isArray(students) ? students : []}
        onClose={() => setAssignTarget(null)}
      />

      {/* Issue Book Dialog */}
      <Dialog open={showBorrowForm} onOpenChange={setShowBorrowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Issue Book</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Book *</Label>
              <Select value={borrowForm.book_id} onValueChange={v => setBorrowForm({ ...borrowForm, book_id: v })}>
                <SelectTrigger data-testid="issue-book-select"><SelectValue placeholder="Select book" /></SelectTrigger>
                <SelectContent>{books.filter(b => (!b.resource_type || b.resource_type === 'physical') && b.available_copies > 0).map(b => <SelectItem key={b.id} value={b.id}>{b.title} ({b.available_copies} avail)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Student *</Label>
              <Select value={borrowForm.student_id} onValueChange={v => setBorrowForm({ ...borrowForm, student_id: v })}>
                <SelectTrigger data-testid="issue-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{(Array.isArray(students) ? students : []).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.admission_number})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Due Date</Label><Input type="date" value={borrowForm.due_date} onChange={e => setBorrowForm({ ...borrowForm, due_date: e.target.value })} /></div>
            <div><Label>Notes</Label><Input value={borrowForm.notes} onChange={e => setBorrowForm({ ...borrowForm, notes: e.target.value })} placeholder="Optional notes" /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => borrowBook.mutate(borrowForm)} disabled={!borrowForm.book_id || !borrowForm.student_id || borrowBook.isPending} data-testid="issue-book-btn">
              {borrowBook.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Issue Book
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish to Bookshop Dialog */}
      <Dialog open={!!publishTarget} onOpenChange={(o) => !o && setPublishTarget(null)}>
        <DialogContent data-testid="publish-to-shop-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Store className="h-4 w-4 text-emerald-600" /> {publishTarget?.bookshop_book_id ? 'Update in Bookshop' : 'Publish to Bookshop'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-slate-50 p-3 text-xs">
              <div className="font-semibold text-slate-800">{publishTarget?.title}</div>
              <div className="text-slate-500">{publishTarget?.author || 'Unknown author'} · {publishTarget?.category || 'Uncategorised'}</div>
              <div className="mt-1 text-slate-500">The Library record stays as-is. A copy is sent to the public Bookshop for purchase / rental.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Purchase price (USD)</Label>
                <Input type="number" min="0" step="0.01" value={publishForm.price} onChange={e => setPublishForm({ ...publishForm, price: parseFloat(e.target.value) || 0 })} data-testid="publish-price-input" />
              </div>
              <div>
                <Label>Rental price (USD)</Label>
                <Input type="number" min="0" step="0.01" value={publishForm.rental_price} onChange={e => setPublishForm({ ...publishForm, rental_price: parseFloat(e.target.value) || 0 })} data-testid="publish-rental-input" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="publish-is-free" checked={publishForm.is_free} onChange={e => setPublishForm({ ...publishForm, is_free: e.target.checked })} data-testid="publish-isfree-check" />
              <Label htmlFor="publish-is-free" className="text-sm">Offer as free download</Label>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={publishForm.status} onValueChange={v => setPublishForm({ ...publishForm, status: v })}>
                <SelectTrigger data-testid="publish-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published (visible in Bookshop)</SelectItem>
                  <SelectItem value="draft">Draft (hidden)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            {publishTarget?.bookshop_book_id && (
              <>
                <Button
                  variant="outline"
                  onClick={() => syncFromShop.mutate(publishTarget.id)}
                  disabled={syncFromShop.isPending}
                  data-testid="sync-from-shop-btn"
                  title="Pull current pricing + status from the Bookshop listing"
                >
                  {syncFromShop.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  Sync from Bookshop
                </Button>
                <Button
                  variant="outline"
                  className="text-amber-700 border-amber-200 hover:bg-amber-50"
                  onClick={() => unpublishFromShop.mutate(publishTarget.id)}
                  disabled={unpublishFromShop.isPending}
                  data-testid="unpublish-from-shop-btn"
                >
                  {unpublishFromShop.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  Hide from Bookshop
                </Button>
              </>
            )}
            <Button onClick={() => publishToShop.mutate({ id: publishTarget.id, body: publishForm })} disabled={!publishTarget || publishToShop.isPending} data-testid="publish-confirm-btn">
              {publishToShop.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {publishTarget?.bookshop_book_id ? 'Update in Bookshop' : 'Publish to Bookshop'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchoolLibrary;

// ==================== READING ASSIGNMENTS ====================

function AssignBookDialog({ tenantId, book, students, onClose }) {
  const qc = useQueryClient();
  const open = !!book;
  const [scope, setScope] = useState('specific'); // 'specific' | 'class'
  const [classId, setClassId] = useState('');
  const [selected, setSelected] = useState({}); // {sid: true}
  const [dueDate, setDueDate] = useState('');
  const [mode, setMode] = useState('either');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (book) {
      setScope('specific'); setClassId(''); setSelected({});
      setDueDate(''); setNotes('');
      setMode(book.resource_type && book.resource_type !== 'physical' ? 'digital' : 'either');
    }
  }, [book]);

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/classes`).catch(() => []),
    enabled: open,
  });

  const visibleStudents = React.useMemo(() => {
    if (scope === 'class' && classId) return students.filter(s => s.class_id === classId);
    return students;
  }, [students, scope, classId]);

  const toggle = (sid) => setSelected(prev => {
    const next = { ...prev };
    if (next[sid]) delete next[sid]; else next[sid] = true;
    return next;
  });

  const selectAll = () => {
    const next = {};
    visibleStudents.forEach(s => { next[s.id] = true; });
    setSelected(next);
  };
  const clearAll = () => setSelected({});

  const assignMutation = useMutation({
    mutationFn: (payload) => api(`/tenants/${tenantId}/library/assignments`, { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: (r) => {
      toast.success(`Assigned to ${r.created} student(s) · ${r.skipped} already assigned`);
      qc.invalidateQueries({ queryKey: ['library-assignments'] });
      onClose && onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const submit = () => {
    if (!book) return;
    const payload = { book_id: book.id, due_date: dueDate, notes, mode };
    if (scope === 'class') {
      if (!classId) { toast.error('Pick a class'); return; }
      payload.class_id = classId;
    } else {
      const ids = Object.keys(selected);
      if (ids.length === 0) { toast.error('Select at least one student'); return; }
      payload.student_ids = ids;
    }
    assignMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="assign-book-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-600" />
            Assign required reading
          </DialogTitle>
        </DialogHeader>
        {book && (
          <div className="space-y-3">
            <div className="p-3 rounded-md bg-slate-50 border text-sm">
              <div className="font-medium text-slate-900">{book.title}</div>
              <div className="text-xs text-slate-500">{book.author || 'Unknown author'} · {book.resource_type || 'physical'}</div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Who to assign</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger data-testid="assign-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="specific">Specific students</SelectItem>
                    <SelectItem value="class">Whole class</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>How to read</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger data-testid="assign-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="either">Either (digital or physical)</SelectItem>
                    <SelectItem value="digital">Digital only</SelectItem>
                    <SelectItem value="physical">Physical copy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {scope === 'class' && (
              <div>
                <Label>Class</Label>
                <Select value={classId} onValueChange={setClassId}>
                  <SelectTrigger data-testid="assign-class-select"><SelectValue placeholder="Choose class" /></SelectTrigger>
                  <SelectContent>
                    {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">All active students in this class will get the assignment.</p>
              </div>
            )}

            {scope === 'specific' && (
              <div className="border rounded-md">
                <div className="p-2 bg-slate-50 text-xs text-slate-600 border-b flex items-center justify-between">
                  <span>{Object.keys(selected).length} of {visibleStudents.length} selected</span>
                  <div className="flex gap-2">
                    <button type="button" className="text-indigo-600 hover:underline" onClick={selectAll}>Select all</button>
                    <button type="button" className="text-slate-500 hover:underline" onClick={clearAll}>Clear</button>
                  </div>
                </div>
                <div className="max-h-64 overflow-auto">
                  {visibleStudents.length === 0 && (
                    <div className="p-6 text-center text-slate-400 text-sm">No students</div>
                  )}
                  {visibleStudents.map(s => (
                    <label key={s.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={!!selected[s.id]} onChange={() => toggle(s.id)} data-testid={`assign-student-cb-${s.id}`} />
                      <span>{s.first_name} {s.last_name}</span>
                      <span className="text-xs text-slate-400 ml-1">{s.admission_number}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Due date</Label>
                <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="assign-due-date" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Chapters 1-5, read by Friday…" data-testid="assign-notes" />
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={submit} disabled={assignMutation.isPending} data-testid="assign-book-submit">
            {assignMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentsPanel({ tenantId, students }) {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const queryParams = new URLSearchParams();
  if (statusFilter) queryParams.set('status', statusFilter);
  const qStr = queryParams.toString() ? `?${queryParams}` : '';

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['library-assignments', tenantId, statusFilter],
    queryFn: () => api(`/tenants/${tenantId}/library/assignments${qStr}`),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }) => api(`/tenants/${tenantId}/library/assignments/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['library-assignments'] }); toast.success('Updated'); },
    onError: e => toast.error(e.message),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/library/assignments/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['library-assignments'] }); toast.success('Unassigned'); },
    onError: e => toast.error(e.message),
  });

  return (
    <div className="space-y-3" data-testid="library-assignments-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1">
          <ClipboardList className="h-4 w-4" /> Required Reading Assignments ({assignments.length})
        </h3>
        <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full max-w-[160px]" data-testid="assignment-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-500" /></div>
      ) : assignments.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400">
          <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-50" />
          No reading assignments yet. Click the green person-plus icon next to a book to assign required reading.
        </CardContent></Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Book</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map(a => (
                <TableRow key={a.id} data-testid={`assignment-row-${a.id}`}>
                  <TableCell>
                    <div className="font-medium text-sm">{a.book_title}</div>
                    <div className="text-xs text-slate-500">{a.book_author}</div>
                  </TableCell>
                  <TableCell className="text-sm">{a.student_name}<div className="text-xs text-slate-400">{a.admission_number}</div></TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{a.mode}</Badge></TableCell>
                  <TableCell className="text-sm">{a.due_date || '-'}</TableCell>
                  <TableCell>
                    <Badge className={
                      a.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      a.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                      a.status === 'cancelled' ? 'bg-slate-100 text-slate-500' :
                      'bg-indigo-100 text-indigo-700'
                    }>{a.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {a.status !== 'completed' && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Mark complete"
                          onClick={() => updateMut.mutate({ id: a.id, body: { status: 'completed' } })}
                          data-testid={`assignment-complete-${a.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Unassign"
                        onClick={() => { if (window.confirm('Remove this assignment?')) deleteMut.mutate(a.id); }}
                        data-testid={`assignment-delete-${a.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

